import { Button } from "@roberttamas/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  ColorSchemeEditor,
  type ColorSchemeValue,
} from "@/components/color-scheme-editor";
import { EditDialog, SettingRow } from "@/components/setting-row";
import { updateSiteConfig } from "@/lib/api";
import {
  DEFAULT_SEED,
  DEFAULT_SEEDS,
  PALETTE_KEYS,
  generatePalette,
  isHexColor,
  type Palette,
  type Seeds,
} from "@/lib/color-scheme";
import { toAppError } from "@/lib/errors";
import { CUSTOM_TEMPLATE_ID } from "@/lib/templates";
import type { ConfigDoc } from "@/lib/types";

/** Colors shown as the read-only summary swatches. */
const SUMMARY_KEYS = ["primary", "secondary", "background", "error", "success", "warning"];

interface ColorSchemeSectionProps {
  siteId: string;
  colorScheme?: ConfigDoc["colorScheme"];
}

/** Small color square used in the read-only summary. */
function Swatch({ color, title }: { color: string; title: string }) {
  return (
    <span
      title={`${title}: ${color}`}
      className="inline-block h-6 w-6 rounded-md border border-border"
      style={{ backgroundColor: color }}
    />
  );
}

/** Builds the editor value from a stored colorScheme (or the default). */
function toValue(colorScheme?: ConfigDoc["colorScheme"]): ColorSchemeValue {
  const palette = colorScheme?.palette ?? generatePalette(DEFAULT_SEEDS);
  const rawSeeds = colorScheme?.seeds;
  if (!rawSeeds) return { seeds: DEFAULT_SEEDS, palette };
  const seed =
    typeof rawSeeds.seed === "string"
      ? rawSeeds.seed
      : typeof rawSeeds.primary === "string"
        ? rawSeeds.primary
        : DEFAULT_SEED;
  return { seeds: { ...(rawSeeds as Partial<Seeds>), seed } as Seeds, palette };
}

export function ColorSchemeSection({ siteId, colorScheme }: ColorSchemeSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["site", siteId] }),
      queryClient.invalidateQueries({ queryKey: ["sites"] }),
    ]);
  }, [queryClient, siteId]);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ColorSchemeValue>(() => toValue(colorScheme));

  const currentPalette: Palette = colorScheme?.palette ?? generatePalette(DEFAULT_SEEDS);

  const openDialog = () => {
    setDraft(toValue(colorScheme));
    setOpen(true);
  };

  const allValid =
    isHexColor(draft.seeds.seed ?? "") &&
    PALETTE_KEYS.every((k) => isHexColor(draft.palette[k] ?? ""));

  const mutation = useMutation({
    mutationFn: (value: ColorSchemeValue) =>
      updateSiteConfig({
        siteId,
        configPatch: {
          colorScheme: value,
          templateId: CUSTOM_TEMPLATE_ID,
        },
      }),
    onSuccess: async () => {
      toast.success(t("colorSection.updated"));
      await invalidate();
      setOpen(false);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  return (
    <>
      <SettingRow
        title={t("colorSection.title")}
        description={t("colorSection.desc")}
        action={
          <Button type="button" variant="ghost" size="sm" onClick={openDialog}>
            {t("common.edit")}
          </Button>
        }
        value={
          <div className="flex flex-wrap items-center gap-1.5">
            {SUMMARY_KEYS.map((key) => (
              <Swatch key={key} color={currentPalette[key] ?? "#000000"} title={key} />
            ))}
          </div>
        }
      />
      <EditDialog
        open={open}
        onOpenChange={setOpen}
        title={t("colorSection.editTitle")}
        description={t("colorSection.editDesc")}
        onSave={() =>
          mutation.mutate({
            seeds: { ...draft.seeds },
            palette: { ...draft.palette },
          })
        }
        saving={mutation.isPending}
        saveDisabled={!allValid}
        contentClassName="sm:max-w-2xl"
      >
        <ColorSchemeEditor value={draft} onChange={setDraft} />
      </EditDialog>
    </>
  );
}
